'use client'

import Cookies from 'js-cookie'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { safeGetItem, safeSetItem } from '../helpers/safeStorage'
import { isKnownLandingBot } from '../lib/landing-bots'
import {
  AGE_NOTICE_COOKIE,
  isNoticePage,
  rememberedAnswer,
} from '../lib/signed-out-showcase'
import Portal from './Portal'

// The 18+ notice before an explicit landing page (ai_app_backend#1441; flag `signed_out_age_notice`, off by default).
// The middleware says when it applies (the AGE_NOTICE_COOKIE cookie, set only while the flag is on and never for a
// known crawler, so search engines keep the page); this shows it once, and the answer is remembered on this device for
// 30 days (safeStorage: a blocked store shows it again next visit, never a blank screen). "Under 18" leaves the site.
const REMEMBERED_KEY = 'pc_age_notice_at'
const LEAVE_TO = 'https://www.google.com/'

export default function AgeNotice() {
  const { t } = useTranslation()
  const pathname = usePathname()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const path = pathname?.replace(/\/+$/, '') || '/'
    if (!isNoticePage(path) || Cookies.get(AGE_NOTICE_COOKIE) !== '1') return
    if (isKnownLandingBot(new Headers({ 'user-agent': navigator.userAgent })))
      return
    if (rememberedAnswer(safeGetItem(REMEMBERED_KEY))) return
    setVisible(true)
  }, [pathname])

  // Nothing behind the notice scrolls while it is up.
  useEffect(() => {
    if (!visible) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [visible])

  if (!visible) return null

  const confirm = () => {
    safeSetItem(REMEMBERED_KEY, String(Date.now()))
    setVisible(false)
  }

  return (
    <Portal>
      {/* Above the dialog tier, like the Korea notice: a page-level interstitial, opaque so the page isn't seen. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-notice-text"
        data-age-notice=""
        className="fixed inset-0 z-[120] flex flex-col items-center justify-center overflow-y-auto bg-[var(--background-bg-color)] px-6 py-16 text-center"
      >
        <div className="w-full max-w-md space-y-6">
          <p className="text-4xl font-bold text-white" aria-hidden="true">
            18+
          </p>
          <p
            id="age-notice-text"
            className="text-base leading-relaxed text-gray-300"
          >
            {t('ageVerification')}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              autoFocus
              onClick={confirm}
              data-track="age_notice.above18"
              className="rounded-full bg-[var(--main-bg-color)] px-6 py-3 font-semibold text-white"
            >
              {t('above18')}
            </button>
            <button
              type="button"
              onClick={() => window.location.replace(LEAVE_TO)}
              data-track="age_notice.below18"
              className="rounded-full border border-gray-600 px-6 py-3 font-semibold text-gray-300"
            >
              {t('below18')}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  )
}
